import { ApiClient } from 'ApiClient';
import { ExternalMenuItemMapping, ExternalOrderLog } from 'interfaces';

class ExternalOrderService {
  public async getLogs(query?: {
    sourceId?: number;
    shopId?: number;
    platform?: string;
    status?: string;
    limit?: number;
  }): Promise<ExternalOrderLog[]> {
    const params: { [key: string]: string | number } = {};
    if (query?.sourceId) params.source_id = query.sourceId;
    if (query?.shopId) params.shop_id = query.shopId;
    if (query?.platform) params.platform = query.platform;
    if (query?.status) params.status = query.status;
    if (query?.limit) params.limit = query.limit;

    return ApiClient.get<ExternalOrderLog[]>('external-order-logs', params);
  }

  public async getMappings(query?: {
    sourceId?: number;
    shopId?: number;
    platform?: string;
    unmappedOnly?: boolean;
  }): Promise<ExternalMenuItemMapping[]> {
    const params: { [key: string]: string | number } = {};
    if (query?.sourceId) params.source_id = query.sourceId;
    if (query?.shopId) params.shop_id = query.shopId;
    if (query?.platform) params.platform = query.platform;
    if (query?.unmappedOnly) params.unmapped_only = 1;

    return ApiClient.get<ExternalMenuItemMapping[]>(
      'external-menu-item-mappings',
      params,
    );
  }

  public updateMapping(
    id: number,
    target:
      | { kind: 'menuItem'; id: number }
      | { kind: 'setMenu'; id: number }
      | { kind: 'unmapped' },
  ) {
    const body: { menu_item_id: number | string; set_menu_id: number | string } = {
      menu_item_id: '',
      set_menu_id: '',
    };
    if (target.kind === 'menuItem') {
      body.menu_item_id = target.id;
    } else if (target.kind === 'setMenu') {
      body.set_menu_id = target.id;
    }
    return ApiClient.put(`external-menu-item-mappings/${id}`, body);
  }

  // ---------------------------------------------------------------
  // Phase 4d outbound Bolt status calls. Each posts to our internal
  // /bolt/orders/{id}/{action} which proxies through to Bolt's API.
  // ---------------------------------------------------------------

  public boltAccept(
    logId: number,
    opts?: {
      orderDelayReason?: string;
      delayTimeInSeconds?: number;
      // Array shape so the form-data serialiser preserves it.
      itemNotes?: Array<{ index: number; note: string }>;
    },
  ) {
    const body: any = {};
    if (opts?.orderDelayReason) body.order_delay_reason = opts.orderDelayReason;
    if (opts?.delayTimeInSeconds != null) {
      body.delay_time_in_seconds = opts.delayTimeInSeconds;
    }
    if (opts?.itemNotes && opts.itemNotes.length) body.item_notes = opts.itemNotes;
    return ApiClient.post(`bolt/orders/${logId}/accept`, body);
  }

  public boltReject(
    logId: number,
    opts: {
      rejectReason:
        | 'do_not_wish_to_serve_this_client'
        | 'too_many_orders'
        | 'other';
      rejectComment?: string;
    },
  ) {
    return ApiClient.post(`bolt/orders/${logId}/reject`, {
      reject_reason: opts.rejectReason,
      reject_comment: opts.rejectComment || '',
    });
  }

  public boltMarkReady(logId: number) {
    return ApiClient.post(`bolt/orders/${logId}/ready`, {});
  }

  public boltMarkPickedUp(logId: number) {
    return ApiClient.post(`bolt/orders/${logId}/picked-up`, {});
  }

  public boltMarkDelivered(logId: number) {
    return ApiClient.post(`bolt/orders/${logId}/delivered`, {});
  }

  // ---------------------------------------------------------------
  // Wolt outbound status calls (2026-06-30). Mirror the Bolt shape so
  // platform-aware components can pick the right method by source.
  // ---------------------------------------------------------------

  public woltAccept(
    logId: number,
    opts?: {
      adjustedPickupTime?: string;
      itemNotes?: Array<{ index: number; note: string }>;
    },
  ) {
    const body: any = {};
    if (opts?.adjustedPickupTime) body.adjusted_pickup_time = opts.adjustedPickupTime;
    if (opts?.itemNotes && opts.itemNotes.length) body.item_notes = opts.itemNotes;
    return ApiClient.post(`wolt/orders/${logId}/accept`, body);
  }

  public woltReject(
    logId: number,
    opts: { reason: string },
  ) {
    return ApiClient.post(`wolt/orders/${logId}/reject`, {
      reason: opts.reason,
    });
  }

  public woltMarkReady(logId: number) {
    return ApiClient.post(`wolt/orders/${logId}/ready`, {});
  }

  public woltMarkDelivered(logId: number) {
    return ApiClient.post(`wolt/orders/${logId}/delivered`, {});
  }

  public woltSelfDeliveryAccept(
    logId: number,
    opts: { totalDeliveryTimeMinutes: number; adjustedPickupTime?: string },
  ) {
    return ApiClient.post(`wolt/orders/${logId}/self-delivery-accept`, {
      total_delivery_time_minutes: opts.totalDeliveryTimeMinutes,
      adjusted_pickup_time: opts.adjustedPickupTime,
    });
  }

  /**
   * Pre-orders are ACKED with confirm-preorder, not accept — Wolt refuses
   * to "move a scheduled order to production" ahead of its preorder time.
   * We still send adjustedPickupTime: our own controller derives
   * accept_prep_time_seconds from it (driving when the kitchen starts),
   * while the outbound Wolt call is confirm-preorder and ignores it.
   */
  public woltConfirmPreorder(
    logId: number,
    opts?: {
      adjustedPickupTime?: string;
      itemNotes?: Array<{ index: number; note: string }>;
    },
  ) {
    const body: any = {};
    if (opts?.adjustedPickupTime) body.adjusted_pickup_time = opts.adjustedPickupTime;
    if (opts?.itemNotes && opts.itemNotes.length) body.item_notes = opts.itemNotes;
    return ApiClient.post(`wolt/orders/${logId}/confirm-preorder`, body);
  }

  // ---------------------------------------------------------------
  // GloriaFood (our own website ordering, 2026-07-27). One-directional:
  // orders auto-fire, EXCEPT ones carrying a global customer comment, which
  // are held for review and fired via this push once staff have distributed
  // the comment onto the dishes as per-item notes.
  // ---------------------------------------------------------------

  public gloriafoodPush(
    logId: number,
    opts?: { itemNotes?: Array<{ index: number; note: string }> },
  ) {
    const body: any = {};
    if (opts?.itemNotes && opts.itemNotes.length) body.item_notes = opts.itemNotes;
    return ApiClient.post(`gloriafood/orders/${logId}/push`, body);
  }

  // ---------------------------------------------------------------
  // Website ordering (our own storefront, 2026-09-05). Fire-on-accept:
  // accept quotes the waiting time AND fires the kitchen in one call.
  // ---------------------------------------------------------------

  public websiteAccept(
    logId: number,
    opts: {
      prepMinutes: number;
      itemNotes?: Array<{ index: number; note: string }>;
    },
  ) {
    const body: any = { prep_minutes: opts.prepMinutes };
    if (opts.itemNotes && opts.itemNotes.length) body.item_notes = opts.itemNotes;
    return ApiClient.post(`website/orders/${logId}/accept`, body);
  }

  public websiteReject(logId: number, opts?: { reason?: string }) {
    return ApiClient.post(`website/orders/${logId}/reject`, {
      reason: opts?.reason || '',
    });
  }

  // Bag left the counter: our driver collected it (delivery -> customer's
  // status page flips to "on its way") or the customer walked out with it
  // (pickup -> their page deliberately stays as it was).
  public websiteMarkPickedUp(logId: number) {
    return ApiClient.post(`website/orders/${logId}/picked-up`, {});
  }

  // Manual online/offline switch for the storefront (one settings row
  // server-side; shaped like the Wolt venue status for UI reuse).
  public websiteGetVenueStatus() {
    return ApiClient.get<any>('website/venue/status');
  }

  public websiteSetVenueOnline(args: { status: 'ONLINE' | 'OFFLINE' }) {
    return ApiClient.post('website/venue/status', { status: args.status });
  }

  // Ordering hours for the storefront. These open and close the site by
  // themselves; the switch above is a pause on top of them. Kept separate
  // from the shop's opening hours, which drive reservations.
  public websiteGetHours() {
    return ApiClient.get<any>('website/hours');
  }

  public websiteSaveHours(args: {
    hours: Array<{
      day_of_week: number;
      // '1' | '0'. ApiClient posts multipart/form-data, so a real boolean
      // would reach PHP as the string "false" and read as truthy.
      closed: string;
      from_time: string;
      to_time: string;
    }>;
    lastOrderOffsetMinutes?: number;
  }) {
    const body: any = { hours: args.hours };
    if (typeof args.lastOrderOffsetMinutes === 'number') {
      body.last_order_offset_minutes = args.lastOrderOffsetMinutes;
    }
    return ApiClient.post<any, any>('website/hours', body);
  }

  // Dish tags (vegetarian / vegan / gluten-free / spicy / family-size) behind
  // the storefront's filter pills and dish-card badges.
  public websiteGetDishTags() {
    return ApiClient.get<any>('website/dish-tags');
  }

  // Storefront promo: one percentage offer, claimable by anyone, no codes.
  public websiteGetDiscounts() {
    return ApiClient.get<any>('website/discounts');
  }

  public websiteSaveDiscount(args: {
    id?: number;
    title: string;
    description?: string;
    percent: number;
    minOrder: number;
    startsAt?: string;
    endsAt?: string;
    isActive: boolean;
  }) {
    return ApiClient.post<any, any>('website/discounts', {
      id: args.id || 0,
      title: args.title,
      description: args.description || '',
      percent: args.percent,
      min_order: args.minOrder,
      starts_at: args.startsAt || '',
      ends_at: args.endsAt || '',
      // '1'/'0', never a raw boolean: multipart turns false into "false".
      is_active: args.isActive ? '1' : '0',
    });
  }

  public websiteSaveDishTags(args: { menuItemId: number; tags: string[] }) {
    return ApiClient.post<any, any>('website/dish-tags', {
      menu_item_id: args.menuItemId,
      tags: args.tags,
    });
  }

  // ---------------------------------------------------------------
  // Wolt Venue operations (2026-07-01)
  // ---------------------------------------------------------------

  public woltGetVenueStatus(shopId: number) {
    return ApiClient.get(`wolt/venues/${shopId}/status`);
  }
  public woltSetVenueOnline(
    shopId: number,
    args: { status: 'ONLINE' | 'OFFLINE'; until?: string },
  ) {
    return ApiClient.post(`wolt/venues/${shopId}/online`, args);
  }
  public woltSetOpeningTimes(
    shopId: number,
    args: { opening_times: any[] },
  ) {
    return ApiClient.post(`wolt/venues/${shopId}/opening-times`, args);
  }
  public woltGetDeliveryProvider(shopId: number) {
    return ApiClient.get(`wolt/venues/${shopId}/delivery-provider`);
  }
  public woltSetDeliveryProvider(
    shopId: number,
    args: { provider: 'SELF_DELIVERY' | 'WOLT' },
  ) {
    return ApiClient.post(`wolt/venues/${shopId}/delivery-provider/update`, args);
  }

  // ---------------------------------------------------------------
  // Wolt Menu operations (2026-07-01)
  // ---------------------------------------------------------------

  public woltGetMenu(shopId: number) {
    return ApiClient.get(`wolt/menu/${shopId}`);
  }
  public woltPushMenu(shopId: number, args?: { menu?: any }) {
    return ApiClient.post(`wolt/menu/${shopId}/push`, args ?? {});
  }
  public woltUpdateMenuItems(
    shopId: number,
    items: Array<{
      external_id: string;
      price?: number;
      discounted_price?: number;
      enabled?: boolean;
      in_stock?: boolean;
    }>,
  ) {
    return ApiClient.post(`wolt/menu/${shopId}/items`, { data: items });
  }
  public woltUpdateMenuOptions(
    shopId: number,
    options: Array<{ external_id: string; price?: number; enabled?: boolean }>,
  ) {
    return ApiClient.post(`wolt/menu/${shopId}/options`, { data: options });
  }
  public woltUpdateInventory(
    shopId: number,
    inventory: Array<{ external_id: string; inventory: number }>,
  ) {
    return ApiClient.post(`wolt/menu/${shopId}/inventory`, { data: inventory });
  }

  // ---------------------------------------------------------------
  // 2-way provider control (2026-06-11). Lets the SPA fully replace
  // the tablet/portal for online/offline + working hours sync.
  // ---------------------------------------------------------------

  public boltGoOnline(shopId: number) {
    return ApiClient.post('bolt/provider/start-working', { shop_id: shopId });
  }

  public boltGoOffline(shopId: number, reason?: string) {
    return ApiClient.post('bolt/provider/pause-orders', {
      shop_id: shopId,
      reason: reason || '',
    });
  }

  public boltSyncHours(shopId: number) {
    return ApiClient.post('bolt/provider/send-hours', { shop_id: shopId });
  }

  public getBoltProviderState(shopId: number) {
    return ApiClient.get<{
      providerId: string;
      providerStatus: 'online' | 'offline' | null;
      providerStatusUpdatedAt: string | null;
    }>('bolt/provider/state', { shop_id: shopId });
  }

  public getBoltOpeningHours() {
    return ApiClient.get<
      Array<{
        id: number;
        dayOfWeek: number;
        fromTime: string | null;
        toTime: string | null;
      }>
    >('bolt-opening-hours');
  }

  public saveBoltOpeningHour(data: {
    id?: number;
    dayOfWeek: number;
    fromTime: string | null;
    toTime: string | null;
  }) {
    if (data.id) {
      return ApiClient.put(`bolt-opening-hours/${data.id}`, data);
    }
    return ApiClient.post('bolt-opening-hours', data);
  }

  public deleteBoltOpeningHour(id: number) {
    return ApiClient.delete(`bolt-opening-hours/${id}`);
  }

  public boltPullMenu(shopId: number) {
    return ApiClient.post<
      { shop_id: number },
      {
        total_skus: number;
        created: number;
        updated: number;
        unchanged: number;
      }
    >('bolt/menu/pull', { shop_id: shopId });
  }

  public getDailySales(days = 14) {
    // NB: ApiClient deep-camelises responses, so snake_case API keys
    // arrive here camelCased (shop_id -> shopId, grand_total -> grandTotal).
    return ApiClient.get<{
      days: Array<{
        date: string;
        isToday: boolean;
        sources: Array<{
          sourceId: number;
          sourceName: string;
          shopId: number | null;
          orderCount: number;
          cashCount: number;
          cashTotal: number;
          cardCount: number;
          cardTotal: number;
          grandTotal: number;
          date: string;
        }>;
      }>;
    }>('external-sales/daily', { days });
  }
}

const singleton = new ExternalOrderService();

export { singleton as ExternalOrderService };
